import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, Download, Loader2 } from "lucide-react";
import { SEO } from "@/components/SEO";
import { PAGE_SEO } from "@/lib/seoData";
import { LOGO } from "@/lib/assets";
import { generatePitchDeckPdf } from "@/lib/pitchDeckPdf";
import { SLIDE_META } from "@/components/pitchDeck/slideMeta";
import { useScrollOverflow } from "@/hooks/useScrollOverflow";
import { ScrollHintFade } from "@/components/ScrollHintFade";
import {
  Slide01Problem,
  Slide02Works,
  Slide03Mechanism,
  Slide04Gap,
  Slide05Today,
  Slide06WhyNow,
  Slide07Traction,
  Slide08Pilot,
  Slide09Clinical,
  Slide09Defensibility,
  Slide10Landscape,
  Slide11Customer,
  Slide12Business,
  Slide13Team,
  Slide14Raise,
  Slide15BigQuestion,
  Slide16Going,
} from "@/components/pitchDeck/slides";

export default function PitchDeck() {
  const [idx, setIdx] = useState(0);
  const [direction, setDirection] = useState(1);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(false);
  const touchStartX = useRef(0);
  const { ref: slideScrollRef, hasMore: hasMoreBelow, onScroll: checkOverflow } = useScrollOverflow();

  const goTo = useCallback(
    (i) => {
      const clamped = Math.max(0, Math.min(SLIDE_META.length - 1, i));
      setDirection(clamped > idx ? 1 : -1);
      setIdx(clamped);
    },
    [idx]
  );
  const goNext = useCallback(() => goTo(idx + 1), [idx, goTo]);
  const goPrev = useCallback(() => goTo(idx - 1), [idx, goTo]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        goPrev();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [goNext, goPrev]);

  const onTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    const dx = e.changedTouches[0].clientX - touchStartX.current;
    if (Math.abs(dx) > 50) {
      if (dx < 0) goNext();
      else goPrev();
    }
  };

  const handleDownload = async () => {
    if (downloading) return;
    setDownloading(true);
    setDownloadError(false);
    try {
      await generatePitchDeckPdf();
    } catch (err) {
      console.error("Pitch deck PDF generation failed:", err);
      setDownloadError(true);
      setTimeout(() => setDownloadError(false), 4000);
    } finally {
      setTimeout(() => setDownloading(false), 500);
    }
  };

  const SLIDES = [
    <Slide01Problem key="problem" />,
    <Slide02Works key="works" />,
    <Slide03Mechanism key="mechanism" />,
    <Slide04Gap key="gap" />,
    <Slide05Today key="today" />,
    <Slide06WhyNow key="whynow" />,
    <Slide07Traction key="traction" />,
    <Slide08Pilot key="pilot" />,
    <Slide09Clinical key="clinical" />,
    <Slide09Defensibility key="defensibility" />,
    <Slide10Landscape key="landscape" />,
    <Slide11Customer key="customer" />,
    <Slide12Business key="business" />,
    <Slide13Team key="team" />,
    <Slide14Raise key="raise" />,
    <Slide15BigQuestion key="bigquestion" />,
    <Slide16Going key="going" />,
  ];

  return (
    <div
      className="fixed inset-0 bg-[#0a0a0a] text-white flex flex-col z-[60]"
      data-testid="pitch-deck-page"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <SEO {...PAGE_SEO["/investors"]} path="/investors" noindex />

      {/* top bar */}
      <div className="flex items-center justify-between px-6 md:px-10 h-14 md:h-16 shrink-0 border-b border-zinc-900">
        <Link to="/" data-testid="pitch-back-home" className="flex items-center gap-2.5 group">
          <img
            src={LOGO}
            alt="WearSpray"
            className="w-7 h-7 object-contain group-hover:scale-110 transition-transform"
          />
          <span className="font-heading text-base font-semibold tracking-tight">
            Wear<span className="text-accent">Spray</span>
          </span>
        </Link>
        <div className="flex items-center gap-4">
          {downloadError && (
            <span className="font-body text-xs text-[#ff2d78]" data-testid="pitch-download-error">
              Couldn't generate PDF, please try again.
            </span>
          )}
          <span className="font-mono text-xs tracking-[0.25em] text-zinc-500" data-testid="pitch-slide-counter">
            {SLIDE_META[idx].n} / {String(SLIDE_META.length).padStart(2, "0")}
          </span>
          <button
            type="button"
            onClick={handleDownload}
            disabled={downloading}
            data-testid="pitch-download-pdf-btn"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.15em] uppercase text-zinc-300 border border-zinc-700 rounded-full px-4 py-2 hover:border-accent hover:text-accent transition-colors disabled:opacity-60"
          >
            {downloading ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            <span className="hidden sm:inline">{downloading ? "Preparing\u2026" : "Download PDF"}</span>
          </button>
        </div>
      </div>

      {/* progress bar */}
      <div className="h-[2px] bg-zinc-900 shrink-0">
        <motion.div
          className="h-full bg-accent"
          animate={{ width: `${((idx + 1) / SLIDE_META.length) * 100}%` }}
          transition={{ duration: 0.4, ease: "easeOut" }}
        />
      </div>

      {/* slide area */}
      <div className="relative flex-1 overflow-hidden">
        <AnimatePresence mode="wait" custom={direction} initial={false}>
          <motion.div
            key={idx}
            ref={slideScrollRef}
            custom={direction}
            initial={{ opacity: 0, x: direction * 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -50 }}
            transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
            onScroll={checkOverflow}
            className="absolute inset-0 flex items-start px-6 md:px-16 lg:px-24 overflow-y-auto py-6 md:py-9"
            data-lenis-prevent
          >
            {SLIDES[idx]}
          </motion.div>
        </AnimatePresence>

        {/* scroll-for-more affordance */}
        <ScrollHintFade show={hasMoreBelow} testid="pitch-scroll-hint" />
      </div>

      {/* bottom nav */}
      <div className="shrink-0 border-t border-zinc-900 px-6 md:px-10 h-16 flex items-center justify-between">
        <button
          type="button"
          onClick={goPrev}
          disabled={idx === 0}
          data-testid="pitch-prev-btn"
          aria-label="Previous slide"
          className="flex items-center justify-center w-11 h-11 rounded-full border border-zinc-700 text-white hover:border-accent hover:text-accent transition-colors disabled:opacity-25 disabled:pointer-events-none"
        >
          <ChevronLeft size={18} />
        </button>

        <div className="flex items-center gap-2">
          {SLIDE_META.map((s, i) => (
            <button
              key={s.id}
              type="button"
              onClick={() => goTo(i)}
              aria-label={`Go to ${s.label}`}
              data-testid={`pitch-dot-${i}`}
              className={`h-1.5 rounded-full transition-all duration-300 ${
                i === idx ? "w-8 bg-accent" : "w-1.5 bg-zinc-700 hover:bg-zinc-500"
              }`}
            />
          ))}
        </div>

        <button
          type="button"
          onClick={goNext}
          disabled={idx === SLIDE_META.length - 1}
          data-testid="pitch-next-btn"
          aria-label="Next slide"
          className="flex items-center justify-center w-11 h-11 rounded-full border border-zinc-700 text-white hover:border-accent hover:text-accent transition-colors disabled:opacity-25 disabled:pointer-events-none"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>
  );
}
